import React, { type PropsWithChildren } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View, Platform, TouchableOpacity } from 'react-native';
import Animated, {
  Easing,
  FadeIn,
  FadeInDown,
  LinearTransition,
  SlideInDown,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme, useResponsive } from '@sg/garnish';

import { useNavigateBack } from '../../../../navigation';
import {
  APPLY_REWARD_LAYOUT_DURATION,
  APPLY_REWARD_ENTRANCE_DURATION,
} from '../../ApplyRewardScreen.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const ApplyRewardModal = (props: ApplyRewardModalProps) => {
  const { children, palette = 'forest' } = props;

  const navigateBack = useNavigateBack();
  const { currentBreakpoint } = useResponsive();
  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();
  const { formatMessage } = useIntl();

  // ─── Derived ─────────────────────────────────────────────────────────

  const isSheet = currentBreakpoint.isXS;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    isSheet ? styles.containerSheet : styles.containerDialog,
  ];

  const sheetDynamicStyles = useStyle(
    () => (isSheet ? { paddingBottom: SHEET_BOTTOM_SPACING + safeAreaBottomInset } : undefined),
    [isSheet, safeAreaBottomInset],
  );

  const panelStyles = [
    palette === 'cream' ? styles.panelCream : styles.panelForest,
    isSheet ? styles.panelSheet : styles.panelDialog,
    sheetDynamicStyles,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <Animated.View entering={BACKDROP_ENTERING} style={styles.backdrop}>
        <TouchableOpacity
          accessibilityLabel={formatMessage(messages.dismissAccessibilityLabel)}
          accessibilityRole="button"
          activeOpacity={0.8}
          style={styles.backdropPressable}
          onPress={navigateBack}
        />
      </Animated.View>

      <Animated.View
        entering={isSheet ? SHEET_ENTERING : DIALOG_ENTERING}
        layout={PANEL_LAYOUT_TRANSITION}
        style={panelStyles}
      >
        {children}
      </Animated.View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SHEET_BOTTOM_SPACING = Platform.select({
  ios: theme.spacing['2'],
  default: theme.spacing['6'],
});

const ENTRANCE_EASING = Easing.out(Easing.cubic);

const DIALOG_WIDTH = 584;
const DIALOG_MAX_HEIGHT = 672;

const PANEL_LAYOUT_TRANSITION = LinearTransition.duration(APPLY_REWARD_LAYOUT_DURATION);

const BACKDROP_ENTERING = FadeIn.duration(APPLY_REWARD_ENTRANCE_DURATION);
const SHEET_ENTERING = SlideInDown.duration(APPLY_REWARD_ENTRANCE_DURATION).easing(ENTRANCE_EASING);
const DIALOG_ENTERING = FadeInDown.duration(APPLY_REWARD_ENTRANCE_DURATION).easing(ENTRANCE_EASING);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerSheet: {
    justifyContent: 'flex-end',
  },
  containerDialog: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing['10'],
  },
  backdrop: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  backdropPressable: {
    flex: 1,
  },
  panelForest: {
    backgroundColor: theme.colors.FOREST,
  },
  panelCream: {
    backgroundColor: theme.colors.CREAM,
  },
  panelSheet: {
    width: '100%',
    borderTopLeftRadius: theme.radius.large,
    borderTopRightRadius: theme.radius.large,
    gap: theme.spacing['10'],
    paddingTop: theme.spacing['12'],
    paddingBottom: theme.spacing['8'],
    paddingHorizontal: theme.spacing['6'],
    justifyContent: 'space-between',
  },
  panelDialog: {
    width: DIALOG_WIDTH,
    maxWidth: '100%',
    maxHeight: DIALOG_MAX_HEIGHT,
    justifyContent: 'center',
    borderRadius: theme.radius.large,
    gap: theme.spacing['10'],
    paddingTop: theme.spacing['12'],
    paddingBottom: theme.spacing['8'],
    paddingHorizontal: theme.spacing['6'],
    boxShadow: '0px 12px 48px 8px rgba(14, 21, 14, 0.12)',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ApplyRewardModalProps = PropsWithChildren<{
  palette?: 'cream' | 'forest';
}>;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dismissAccessibilityLabel: {
    defaultMessage: 'Dismiss',
    description: 'Click to Claim | Bottom sheet | Dismiss backdrop accessibility label',
  },
});
